import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useHOA } from '../contexts/HOAContext';
import { EmptyState } from '../components/ui';
import { inlineToPlainText, parseLegalMarkdown } from '../lib/legalMarkdown';
import {
  docTypeFromSlug,
  getPublishedLegalDocument,
  parseLegalPinParams,
  parseVersionParam,
} from '../services/legalDocumentsService';

/**
 * Public legal document page: /legal/terms, /legal/privacy,
 * /legal/electronic-notice (optionally ?v=<version>). No sign-in needed.
 * Links from the registration page are pinned: &h=<hoa_id> selects the invite's
 * HOA (used instead of the hostname's HOA when it is a valid uuid) and
 * &d=<document_id> selects exactly the document the server returned (the
 * server still checks type, HOA scope and version, and returns published text
 * only). Without pins the tenant comes from HOAProvider (hostname lookup).
 * The text is rendered as React elements only — never as raw HTML.
 */

const formatEffectiveDate = (isoDate) => {
  if (!isoDate) return '';
  const date = new Date(`${String(isoDate).slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return String(isoDate);
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const renderInline = (tokens, keyPrefix) =>
  (tokens || []).map((token, index) => {
    const key = `${keyPrefix}-${index}`;
    switch (token.type) {
      case 'strong':
        return (
          <strong key={key} className="font-bold text-primary-dark">
            {renderInline(token.children, key)}
          </strong>
        );
      case 'em':
        return (
          <em key={key} className="italic">
            {renderInline(token.children, key)}
          </em>
        );
      case 'link': {
        const external = token.href.toLowerCase().startsWith('https://');
        return (
          <a
            key={key}
            href={token.href}
            className="font-bold text-secondary underline underline-offset-2 hover:brightness-110 break-words"
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
          >
            {renderInline(token.children, key)}
          </a>
        );
      }
      default:
        return <React.Fragment key={key}>{token.text}</React.Fragment>;
    }
  });

const headingClass = {
  1: 'text-2xl font-bold tracking-tight text-primary-dark mt-10 mb-4 first:mt-0',
  2: 'text-xl font-bold tracking-tight text-primary-dark mt-8 mb-3 first:mt-0',
  3: 'text-base font-bold text-primary-dark mt-6 mb-2 first:mt-0',
};

const renderBlock = (block, index) => {
  const key = `b${index}`;
  switch (block.type) {
    case 'heading': {
      // The page title is the <h1>; markdown headings sit one level below it.
      const Tag = block.level === 1 ? 'h2' : block.level === 2 ? 'h3' : 'h4';
      return (
        <Tag key={key} className={headingClass[block.level]}>
          {renderInline(block.children, key)}
        </Tag>
      );
    }
    case 'list': {
      const Tag = block.ordered ? 'ol' : 'ul';
      return (
        <Tag
          key={key}
          start={block.ordered && block.start !== 1 ? block.start : undefined}
          className={`${block.ordered ? 'list-decimal' : 'list-disc'} pl-6 my-4 space-y-2 text-base font-medium text-primary-dark/90 leading-relaxed marker:text-secondary`}
        >
          {block.items.map((item, itemIndex) => (
            <li key={`${key}-${itemIndex}`}>{renderInline(item, `${key}-${itemIndex}`)}</li>
          ))}
        </Tag>
      );
    }
    case 'hr':
      return <hr key={key} className="my-8 border-amber-100" />;
    default:
      return (
        <p key={key} className="my-4 text-base font-medium text-primary-dark/90 leading-relaxed">
          {renderInline(block.children, key)}
        </p>
      );
  }
};

/** Renders parsed legal markdown. Skips a leading "# Title" that repeats the page title. */
export const LegalDocumentBody = ({ markdown, title }) => {
  const blocks = useMemo(() => {
    const parsed = parseLegalMarkdown(markdown);
    const first = parsed[0];
    if (
      first &&
      first.type === 'heading' &&
      first.level === 1 &&
      title &&
      inlineToPlainText(first.children).trim().toLowerCase() === String(title).trim().toLowerCase()
    ) {
      return parsed.slice(1);
    }
    return parsed;
  }, [markdown, title]);

  return <div className="min-w-0 break-words">{blocks.map(renderBlock)}</div>;
};

const Spinner = () => (
  <div className="flex items-center justify-center py-20" aria-busy="true">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-secondary" />
    <span className="sr-only">Loading document…</span>
  </div>
);

const StateCard = ({ icon, title, body, action }) => (
  <div className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30">
    <EmptyState icon={icon} title={title} body={body}>
      {action}
    </EmptyState>
  </div>
);

const LegalDocumentPage = () => {
  const { docSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { hoa, isLoading: hoaLoading } = useHOA();
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const docType = docTypeFromSlug(docSlug);
  const versionParam = parseVersionParam(searchParams.get('v'));
  const requestedVersion = versionParam.version ?? null;
  const knownRequest = Boolean(docType) && !versionParam.invalid;
  const { hoaId: pinnedHoaId, documentId } = parseLegalPinParams(searchParams);
  const hoaId = pinnedHoaId || hoa?.id || null;
  // A pinned HOA does not need to wait for the hostname lookup.
  const waitForHoa = hoaLoading && !pinnedHoaId;
  // Never label another HOA's document with the hostname HOA's name.
  const hoaName = !pinnedHoaId || pinnedHoaId === hoa?.id ? hoa?.name || '' : '';

  const [state, setState] = useState({ status: 'loading', doc: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!knownRequest) {
      setState({ status: 'not_found', doc: null });
      return undefined;
    }
    if (waitForHoa) return undefined;

    let active = true;
    setState({ status: 'loading', doc: null });
    getPublishedLegalDocument({ hoaId, docType, version: requestedVersion, documentId }).then(
      ({ data, error }) => {
        if (!active) return;
        if (error) setState({ status: 'error', doc: null });
        else if (!data) setState({ status: 'unpublished', doc: null });
        else setState({ status: 'ready', doc: data });
      }
    );
    return () => {
      active = false;
    };
  }, [knownRequest, waitForHoa, hoaId, docType, requestedVersion, documentId, attempt]);

  useEffect(() => {
    if (state.status !== 'ready' || !state.doc?.title) return undefined;
    const previous = document.title;
    document.title = hoaName ? `${state.doc.title} · ${hoaName}` : state.doc.title;
    return () => {
      document.title = previous;
    };
  }, [state, hoaName]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  // A signed-in reader gets a way back to the portal. When they arrived from the portal
  // footer, step back in history so they land on the tab they were using.
  const cameFromPortal = location.state?.fromPortal === true;
  const handleBackToPortal = (event) => {
    if (!cameFromPortal) return;
    event.preventDefault();
    navigate(-1);
  };

  let content;
  if (state.status === 'not_found') {
    content = (
      <StateCard
        icon="description"
        title="Document not found"
        body="We couldn't find that document. Check the link, or return to the website."
      />
    );
  } else if (state.status === 'unpublished') {
    content = (
      <StateCard
        icon="description"
        title="This document hasn't been published yet"
        body="Please check back soon."
      />
    );
  } else if (state.status === 'error') {
    content = (
      <StateCard
        icon="wifi_off"
        title="We couldn't load this document"
        body="Check your internet connection, then try again."
        action={
          <button
            type="button"
            onClick={retry}
            className="inline-flex items-center justify-center gap-2 min-h-[44px] bg-secondary text-white px-8 py-3.5 rounded-full font-bold hover:brightness-110 active:scale-95 transition-all shadow-warm"
          >
            Try Again
          </button>
        }
      />
    );
  } else if (state.status === 'ready') {
    const { doc } = state;
    content = (
      <article
        className="bg-white rounded-2xl p-6 sm:p-10 warm-tactile-shadow border border-amber-100/30"
        aria-labelledby="legal-document-title"
      >
        <p className="text-[10px] font-bold uppercase tracking-tighter text-slate-500">
          Version {doc.version}
          {doc.effective_date ? ` · Effective ${formatEffectiveDate(doc.effective_date)}` : ''}
        </p>
        <h1
          id="legal-document-title"
          className="text-3xl font-bold tracking-tight text-primary-dark mt-2 mb-6 break-words"
        >
          {doc.title}
        </h1>
        <LegalDocumentBody markdown={doc.body_markdown} title={doc.title} />
      </article>
    );
  } else {
    content = (
      <div className="bg-white rounded-2xl warm-tactile-shadow border border-amber-100/30">
        <Spinner />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-main-bg py-10 sm:py-12 px-4 sm:px-6">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-sidebar-bg mb-4">
            <span className="material-symbols-outlined text-white text-2xl" aria-hidden="true">
              landscape
            </span>
          </div>
          {hoaName && (
            <p className="text-xs font-bold uppercase tracking-widest text-slate-500">{hoaName}</p>
          )}
        </div>

        <section className="bg-section-bg p-3 sm:p-8 rounded-[2.5rem]">{content}</section>

        <div className="mt-8 flex flex-col items-center gap-3 text-center">
          {isAuthenticated && (
            <Link
              to="/portal"
              onClick={handleBackToPortal}
              className="inline-flex items-center justify-center min-h-[44px] text-sm font-bold text-secondary hover:underline rounded-full px-4 py-2"
            >
              Back to Portal
            </Link>
          )}
          <Link
            to="/"
            className="inline-flex items-center justify-center min-h-[44px] text-sm font-bold text-secondary hover:underline rounded-full px-4 py-2"
          >
            Return to Website
          </Link>
          {hoaName && <p className="text-xs font-medium text-slate-500">{hoaName}</p>}
        </div>
      </div>
    </main>
  );
};

export default LegalDocumentPage;
